import React, {FC, useState} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {defineMessage, Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {AsyncStatusesEnum} from '../../../../../libs/shared/core/src/helpers/types/asyncStatuses';
import {LoadedMessageModel} from '../../../../../libs/shared/core/src/models/api/messageModel';
import {ViewerPanelId} from '../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {TextAreaBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textAreaBox';
import {Button} from '../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {Dialog} from '../../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogFooter} from '../../../../../libs/shared/ui/src/components/dialogs/dialogFooter';
import {DialogHeader} from '../../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {Icon} from '../../../../../libs/shared/ui/src/components/icon/icon';
import {AsyncLoader} from '../../../../../libs/shared/ui/src/components/loaders/asyncLoader';
import {Skeleton} from '../../../../../libs/shared/ui/src/components/utility/skeleton';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {useAppDispatch} from '../../../../helpers/appReduxHelpers';
import {getColorHighlight} from '../../../../helpers/models/highlightHelpers';
import {VisualRecipientMap} from '../../../../helpers/models/recipientHelpers';
import {ConversationAiMarkModel} from '../../../../models/api/conversationAiMarkModel';
import {ConversationModel} from '../../../../models/api/conversationModel';
import {TagModel} from '../../../../models/api/tagModel';
import {AiTaggingReviewManualDiscardAsyncAction} from '../../../../settings/rules/ruleEditor/recipes/aiTagging/state/aiTaggingEditorActions';
import {showAiTaggingErrorBannerAction} from '../../../../store/panels/app/banners/bannersActions';
import {AiTaggingErrorBannerTypesEnum} from '../../../../store/panels/app/banners/bannersState';
import {
  AiHarvestingMessageView,
  AiHarvestingMessageViewHeaderVariantsEnum,
} from '../../../reviews/aiHarvestingMessageView';
import {ActivityTagPillContent} from '../activity/activityTagPill/activityTagPillContent';
import {ConversationHeaderPill} from '../conversation/header/conversationHeaderPill';

/*
 * Constants.
 */

const dialogWidth = 600; // px.
const dialogHeight = 650; // px.

/*
 * Props.
 */

export interface AiTaggingInaccurateDialogProps {
  panelId: ViewerPanelId;
  tag: TagModel;
  tagsPath: ReadonlyArray<TagModel>;
  asyncStatus: AsyncStatusesEnum;
  message?: LoadedMessageModel;
  recipients: VisualRecipientMap;
  conversation: ConversationModel;
  aiMark: ConversationAiMarkModel;
  onRequestClose: () => void;
  onRetryClick: () => void;
}

/*
 * Style.
 */

const StyledHeaderWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 4px;
  align-items: center;
`;

const StyledDialogContentDiv = styled.div`
  display: flex;
  flex-flow: column;
  flex: 1;
  margin: 20px;
`;

const StyledMessageWrapperDiv = styled.div`
  display: flex;
  flex-flow: column;
  flex: 1;
`;

const StyledMessageNoteDiv = styled.div`
  font-weight: ${fontWeights.semibold};
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade70};
  margin: 4px 0 6px 0;
`;

const StyledFooterMessageDiv = styled.div`
  display: flex;
  flex: 1;
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade60};
  align-items: center;
`;

const StyledDefaultPaddingDiv = styled.div`
  padding: 16px;
  display: grid;
  flex: 1;
`;

/*
 * Component.
 */

export const AiTaggingInaccurateDialog: FC<AiTaggingInaccurateDialogProps> = (props) => {
  const {
    panelId,
    tag,
    tagsPath,
    asyncStatus,
    recipients,
    message,
    conversation,
    aiMark,
    onRequestClose,
    onRetryClick,
  } = props;
  const localize = useLocalize();
  const dispatch = useAppDispatch();

  const [reason, setReason] = useState('');
  const [isRemoving, setIsRemoving] = useState(false);
  const isReportDisabled = asyncStatus === AsyncStatusesEnum.LOADING || !reason.trim() || isRemoving;

  const color = getColorHighlight(tag);

  const onRemoveClickAsync = async () => {
    setIsRemoving(true);
    try {
      await dispatch(AiTaggingReviewManualDiscardAsyncAction(conversation.id, aiMark, reason));
    } catch {
      dispatch(showAiTaggingErrorBannerAction(AiTaggingErrorBannerTypesEnum.MANUAL_FEEDBACK_FAILED));
    } finally {
      setIsRemoving(false);
    }
  };

  const onRequestCloseWrapped = () => {
    if (!isRemoving) {
      onRequestClose();
    }
  };

  const renderContent = () => {
    if (asyncStatus === AsyncStatusesEnum.FAILED) {
      return (
        <StyledDefaultPaddingDiv>
          <AsyncLoader
            size={VisualSizesEnum.LARGE}
            status={AsyncStatusesEnum.FAILED}
            error={<Trans id="ai-tagging-inaccurate-dialog-preview-error">Unable to load message</Trans>}
            onRetryClick={onRetryClick}
          />
        </StyledDefaultPaddingDiv>
      );
    }
    if (asyncStatus === AsyncStatusesEnum.LOADING || !message) {
      return (
        <StyledMessageWrapperDiv>
          <StyledMessageNoteDiv>
            <Skeleton height="15px" width="300px" />
          </StyledMessageNoteDiv>
          <Skeleton borderRadius="16px" />
        </StyledMessageWrapperDiv>
      );
    }
    return (
      <StyledMessageWrapperDiv>
        <StyledMessageNoteDiv>
          <Trans id="ai-tagging-inaccurate-dialog-message">This tag was applied based on this message</Trans>
        </StyledMessageNoteDiv>
        <AiHarvestingMessageView
          panelId={panelId}
          variant={AiHarvestingMessageViewHeaderVariantsEnum.SENDER}
          message={message}
          recipients={recipients}
        />
      </StyledMessageWrapperDiv>
    );
  };

  return (
    <Dialog onRequestClose={onRequestCloseWrapped} width={dialogWidth} height={dialogHeight}>
      <DialogHeader title={<Trans id="ai-tagging-inaccurate-dialog-title">Report inaccurate tag</Trans>} />
      <StyledDialogContentDiv>
        <TextAreaBox
          placeholder={localize(
            defineMessage({
              id: 'ai-tagging-inaccurate-dialog-placeholder',
              message: 'Enter reason',
            }),
          )}
          isRequired
          value={reason}
          onChange={setReason}
          label={
            <StyledHeaderWrapperDiv>
              <Trans id="ai-tagging-inaccurate-dialog-header-1">Why is</Trans>
              <ConversationHeaderPill
                size={VisualSizesEnum.MEDIUM}
                color={color}
                renderIcon={() => <Icon name="sparklesAiFilledTiny" />}
                shouldRenderIconWithInvertedBackground
              >
                <ActivityTagPillContent tagsPath={tagsPath} />
              </ConversationHeaderPill>
              <Trans id="ai-tagging-inaccurate-dialog-header-2">inaccurate?</Trans>
            </StyledHeaderWrapperDiv>
          }
        />
        {renderContent()}
      </StyledDialogContentDiv>
      <DialogFooter hasSeparator>
        <StyledFooterMessageDiv>
          <Trans id="ai-tagging-inaccurate-dialog-footer-message">Tag will be removed after reporting</Trans>
        </StyledFooterMessageDiv>
        <ButtonGroup>
          <Button onClick={onRequestCloseWrapped} isDisabled={isRemoving}>
            <Trans id="ai-tagging-inaccurate-dialog-cancel-button">Cancel</Trans>
          </Button>
          <Button
            variant={ButtonVariantsEnum.PRIMARY}
            isDisabled={isReportDisabled}
            onClick={onRemoveClickAsync}
            renderTooltip={() => {
              if (!isReportDisabled) {
                return null;
              }

              return <Trans id="ai-tagging-inaccurate-dialog-tooltip">A reason is required</Trans>;
            }}
          >
            <Trans id="ai-tagging-inaccurate-dialog-report-button">Report</Trans>
          </Button>
        </ButtonGroup>
      </DialogFooter>
    </Dialog>
  );
};
